// `.deckai` — a deck as a single file (TODO §12.6).
//
// A browser-only user has no other backup path: their decks live in one origin's IndexedDB,
// which "clear site data" wipes without ceremony. This is also the interchange format the
// desktop shell (§14) will open and save.
//
// Layout — a plain zip:
//   manifest.json          { format, version, assets: [{ assetId, mime, path }] }
//   deck.json              the DeckDocument, verbatim
//   grids/<docId>.json     one GriddDocument per grid body
//   diagrams/<docId>.json  one DiagramDocument per diagram body (format 2, TODO §21/§12.3)
//   gantts/<docId>.json    one GanttDocument per gantt body (format 3, TODO §26/§9)
//   charts/<docId>.json    one ChartDocument per chart body (format 5, TODO §15/§9)
//   assets/<assetId>.<ext> the raw image blobs
//   nuggets/<id>.json      one NuggetRecord per linked knowledge nugget (format 4, §17.5.3)
//   nuggets/<id>/grids/<docId>.json, /diagrams/…, /gantts/…, /charts/…, /assets/<assetId>.<ext>
//                          that nugget's own bodies and blobs, so the deck opens WHOLE on a
//                          machine that has never seen the library
//
// The manifest carries each asset's MIME because the file extension can't: Gridd exports
// and pasted screenshots arrive with types the extension map doesn't round-trip cleanly,
// and `ImageContent.mime` is what the renderer actually reads.
//
// Import ALWAYS goes through `remapBundle` (§12.6.2) — it creates a new deck and never
// overwrites one, not even when the file came from this very database.

import { unzipSync, zipSync, strFromU8, strToU8 } from "fflate";

import {
  readDeckBundle,
  remapBundle,
  takeAdoptedNuggets,
  writeDeckBundle,
  type BundleAsset,
  type DeckBundle,
  type NuggetBundle,
} from "@/lib/deckBundle";
import { normalizePyramid } from "@/lib/pyramid";
import { exportFileName, saveFile } from "@/lib/saveFile";
import { migrateDeck } from "@/store/migrate";
import type { ChartDocument } from "@/types/chart";
import type { DiagramDocument } from "@/types/diagram";
import type { GanttDocument } from "@/types/gantt";
import type { DeckDocument, GriddDocument } from "@/types/document";
import type { NuggetRecord } from "@/types/nugget";

export const DECK_FILE_EXTENSION = ".deckai";

const FORMAT = "deckai";
// §21/§12.3 **2** — the archive gained `diagrams/<docId>.json`. Reading is fully
// backward-compatible: a version-1 file simply has no `diagrams/` entries and `zipToBundle`
// produces an empty map, which is exactly what a deck with no diagrams looks like anyway. The
// number is bumped because the FORMAT changed, not because the reader needs it — and so a
// future reader that DOES need to tell them apart has something to read. Nothing checks it on
// import: refusing a file we can read perfectly well would be the wrong kind of strict.
// §26/§9 **3** — the archive gained `gantts/<docId>.json`, on exactly the terms `diagrams/`
// arrived on: a version-1 or -2 file has no such entries, `zipToBundle` produces an empty map,
// and that is indistinguishable from a deck with no gantts. Still nothing checks the number on
// import.
// §15/§9 **5** — the archive gained `charts/<docId>.json`, on exactly the terms `gantts/` and
// `diagrams/` arrived on. 4 was §17's nugget prefixes; 5 is this. Reading stays fully
// backward-compatible and nothing checks the number on import, for the reason above.
//
// A chart's baked look is NOT in here and does not need to be: the stylesheet travels inside
// `deck.json` (§6.2), and colours are stored as names resolved against it, so an imported deck
// paints its charts from the sheet that came with it (§13.5's mechanism, exercised by import).
const FORMAT_VERSION = 5;

interface Manifest {
  format: string;
  version: number;
  assets: { assetId: string; mime: string; path: string }[];
  // §17.5.3 A nugget's blobs need the same MIME record a deck's do, and they cannot go in
  // the list above: an import must know which owner to file each blob under, and a flat list
  // cannot say. Absent in a version-1–3 archive.
  nuggetAssets?: {
    nuggetId: string;
    assetId: string;
    mime: string;
    path: string;
  }[];
}

// Extension purely for human legibility when someone unzips the file by hand; the MIME in
// the manifest is what import actually trusts.
const EXT_BY_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/avif": "avif",
};

function extFor(mime: string): string {
  return EXT_BY_MIME[mime] ?? "bin";
}

// A filesystem-safe name derived from the deck title, matching the HTML export's rule.
export function deckFileName(title: string): string {
  return exportFileName(title, DECK_FILE_EXTENSION.replace(/^\./, ""));
}

// Serialize a bundle to zip bytes. Split out from `exportDeckFile` so a caller that already
// holds a bundle (the desktop shell's "Save As", later) needn't round-trip through storage.
export async function bundleToZip(bundle: DeckBundle): Promise<Uint8Array> {
  const files: Record<string, Uint8Array> = {};
  const manifest: Manifest = {
    format: FORMAT,
    version: FORMAT_VERSION,
    assets: [],
  };

  files["deck.json"] = strToU8(JSON.stringify(bundle.deck));
  for (const [docId, doc] of Object.entries(bundle.grids)) {
    files[`grids/${docId}.json`] = strToU8(JSON.stringify(doc));
  }
  // §21/§12.3 Diagram bodies travel as their own JSON files, like grids. The images inside
  // them need nothing here: they are ordinary `assets` rows (§4.4.2), so the asset loop below
  // already carries them and the manifest already records their MIME.
  for (const [docId, doc] of Object.entries(bundle.diagrams ?? {})) {
    files[`diagrams/${docId}.json`] = strToU8(JSON.stringify(doc));
  }
  // §26/§9 Gantt bodies travel as their own JSON files too, and carry nothing but JSON — no
  // asset loop to coordinate with, unlike diagrams.
  for (const [docId, doc] of Object.entries(bundle.gantts ?? {})) {
    files[`gantts/${docId}.json`] = strToU8(JSON.stringify(doc));
  }
  // §15/§9 Chart bodies, on the same terms as gantts: pure JSON, no asset loop.
  for (const [docId, doc] of Object.entries(bundle.charts ?? {})) {
    files[`charts/${docId}.json`] = strToU8(JSON.stringify(doc));
  }
  for (const asset of bundle.assets) {
    const path = `assets/${asset.assetId}.${extFor(asset.mime)}`;
    files[path] = new Uint8Array(await asset.blob.arrayBuffer());
    manifest.assets.push({ assetId: asset.assetId, mime: asset.mime, path });
  }
  // §17.5.3 Each linked nugget: its record, then everything it owns, under its own prefix.
  // The prefix is what keeps the owner legible on the way back in — a nugget's grid body and
  // a deck's are the same JSON, and only the path says which is which.
  for (const nugget of bundle.nuggets ?? []) {
    const { nuggetId } = nugget.record;
    files[`nuggets/${nuggetId}.json`] = strToU8(JSON.stringify(nugget.record));
    for (const [docId, doc] of Object.entries(nugget.grids)) {
      files[`nuggets/${nuggetId}/grids/${docId}.json`] = strToU8(
        JSON.stringify(doc),
      );
    }
    for (const [docId, doc] of Object.entries(nugget.diagrams)) {
      files[`nuggets/${nuggetId}/diagrams/${docId}.json`] = strToU8(
        JSON.stringify(doc),
      );
    }
    for (const [docId, doc] of Object.entries(nugget.gantts)) {
      files[`nuggets/${nuggetId}/gantts/${docId}.json`] = strToU8(
        JSON.stringify(doc),
      );
    }
    for (const [docId, doc] of Object.entries(nugget.charts ?? {})) {
      files[`nuggets/${nuggetId}/charts/${docId}.json`] = strToU8(
        JSON.stringify(doc),
      );
    }
    for (const asset of nugget.assets) {
      const path = `nuggets/${nuggetId}/assets/${asset.assetId}.${extFor(asset.mime)}`;
      files[path] = new Uint8Array(await asset.blob.arrayBuffer());
      (manifest.nuggetAssets ??= []).push({
        nuggetId,
        assetId: asset.assetId,
        mime: asset.mime,
        path,
      });
    }
  }

  files["manifest.json"] = strToU8(JSON.stringify(manifest));

  // JSON compresses well; the image blobs are already compressed formats, so level 6 on
  // the whole archive is a fair trade rather than a per-entry decision.
  return zipSync(files, { level: 6 });
}

// §12.6.1 Export one deck to a `.deckai` file the browser downloads. Returns false when the
// deck is gone (deleted in another page since the menu was opened).
export async function exportDeckFile(deckId: string): Promise<boolean> {
  const bundle = await readDeckBundle(deckId);
  if (!bundle) return false;
  const zipped = await bundleToZip(bundle);
  // Copy into a fresh ArrayBuffer: fflate may hand back a view onto a larger pooled buffer,
  // and Blob would otherwise take the whole thing.
  // §14.7.4 In the desktop shell this is the OS save panel ("Save a Copy…"); on the web it is
  // the usual download. Either way the deck itself stays in IndexedDB — the file is a copy,
  // never the live store (§14.7.5).
  await saveFile(
    new Blob([zipped.slice()], { type: "application/zip" }),
    deckFileName(bundle.deck.title),
    "application/zip",
  );
  return true;
}

export class DeckFileError extends Error {}

// Parse `.deckai` bytes into a bundle, still carrying the file's original ids. Throws
// `DeckFileError` with a message fit to show the user — a bad file is a normal thing for
// someone to hand us, not an exceptional one.
export function zipToBundle(bytes: Uint8Array): DeckBundle {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes);
  } catch {
    throw new DeckFileError("That file isn't a deck archive.");
  }

  const deckRaw = entries["deck.json"];
  if (!deckRaw) throw new DeckFileError("The archive has no deck.json.");

  let deck: DeckDocument;
  try {
    deck = JSON.parse(strFromU8(deckRaw)) as DeckDocument;
  } catch {
    throw new DeckFileError("The deck inside the archive is corrupt.");
  }
  if (!deck || typeof deck !== "object" || !Array.isArray(deck.slides)) {
    throw new DeckFileError("The deck inside the archive is corrupt.");
  }
  // A file written by an older build is exactly the case migration exists for, so it runs
  // on the way in rather than leaving a stale schema to be discovered at hydration.
  deck = migrateDeck(deck);
  // §18.1.3 …and the pyramid's invariants are re-established even when the file is ALREADY
  // current, which migration returns untouched by design. A `.deckai` is an external file:
  // it may have been hand-edited, or written by a build whose ordering rules differ from
  // this one's, and "import" is on the list of mutations that must preserve the invariants.
  deck = { ...deck, slides: normalizePyramid(deck.slides) };

  const grids: Record<string, GriddDocument> = {};
  for (const [path, raw] of Object.entries(entries)) {
    if (!path.startsWith("grids/") || !path.endsWith(".json")) continue;
    const docId = path.slice("grids/".length, -".json".length);
    try {
      grids[docId] = JSON.parse(strFromU8(raw)) as GriddDocument;
    } catch {
      // One unreadable grid body loses that grid, not the whole deck.
      console.warn("[deck-file] skipping unreadable grid", path);
    }
  }

  // §21/§12.3 Absent in a version-1 archive, which reads as a deck with no diagrams.
  const diagrams: Record<string, DiagramDocument> = {};
  for (const [path, raw] of Object.entries(entries)) {
    if (!path.startsWith("diagrams/") || !path.endsWith(".json")) continue;
    const docId = path.slice("diagrams/".length, -".json".length);
    try {
      diagrams[docId] = JSON.parse(strFromU8(raw)) as DiagramDocument;
    } catch {
      // One unreadable diagram loses that drawing, not the whole deck — its window then
      // renders the "drawing is missing" state rather than the import failing.
      console.warn("[deck-file] skipping unreadable diagram", path);
    }
  }

  // §26/§9 Absent in a version-1 or -2 archive, which reads as a deck with no gantts.
  const gantts: Record<string, GanttDocument> = {};
  for (const [path, raw] of Object.entries(entries)) {
    if (!path.startsWith("gantts/") || !path.endsWith(".json")) continue;
    const docId = path.slice("gantts/".length, -".json".length);
    try {
      gantts[docId] = JSON.parse(strFromU8(raw)) as GanttDocument;
    } catch {
      // One unreadable schedule loses that chart, not the whole deck — its window renders
      // the "schedule is missing" state instead.
      console.warn("[deck-file] skipping unreadable gantt", path);
    }
  }

  // §15/§9 Absent in a version-1–4 archive, which reads as a deck with no charts.
  const charts: Record<string, ChartDocument> = {};
  for (const [path, raw] of Object.entries(entries)) {
    if (!path.startsWith("charts/") || !path.endsWith(".json")) continue;
    const docId = path.slice("charts/".length, -".json".length);
    try {
      charts[docId] = JSON.parse(strFromU8(raw)) as ChartDocument;
    } catch {
      // One unreadable chart loses that window, not the whole deck — it renders the
      // "chart data is missing" state instead.
      console.warn("[deck-file] skipping unreadable chart", path);
    }
  }

  // §17.5.3 The nuggets, each with its own bodies. Absent in a version-1–3 archive, which
  // reads as a deck with no linked slides — and a deck whose slides DO carry links but whose
  // archive has no nuggets is fine too: those links are dropped on the first open (§17.2.5)
  // and the materialized slides stand on their own, which is exactly the guarantee §17.1.6
  // was chosen for.
  const nuggets: NuggetBundle[] = [];
  const nuggetAssetsById = new Map<string, BundleAsset[]>();

  const assets: BundleAsset[] = [];
  const manifestRaw = entries["manifest.json"];
  if (manifestRaw) {
    try {
      const manifest = JSON.parse(strFromU8(manifestRaw)) as Manifest;
      for (const entry of manifest.assets ?? []) {
        const raw = entries[entry.path];
        if (!raw) continue;
        assets.push({
          assetId: entry.assetId,
          mime: entry.mime,
          blob: new Blob([raw.slice()], { type: entry.mime }),
        });
      }
      for (const entry of manifest.nuggetAssets ?? []) {
        const raw = entries[entry.path];
        if (!raw) continue;
        const list = nuggetAssetsById.get(entry.nuggetId) ?? [];
        list.push({
          assetId: entry.assetId,
          mime: entry.mime,
          blob: new Blob([raw.slice()], { type: entry.mime }),
        });
        nuggetAssetsById.set(entry.nuggetId, list);
      }
    } catch {
      // No manifest, no images. The deck still opens; image windows show their placeholder.
      console.warn("[deck-file] unreadable manifest; importing without images");
    }
  }

  for (const [path, raw] of Object.entries(entries)) {
    // Only the record itself — `nuggets/<id>.json`. The bodies live one directory deeper and
    // are collected per nugget below, so the slash check is what tells them apart.
    if (!path.startsWith("nuggets/") || !path.endsWith(".json")) continue;
    const rest = path.slice("nuggets/".length, -".json".length);
    if (rest.includes("/")) continue;
    let record: NuggetRecord;
    try {
      record = JSON.parse(strFromU8(raw)) as NuggetRecord;
    } catch {
      // One unreadable nugget loses the SHARING, not the slide: the deck's own copy is
      // materialized and complete, and its link falls away on the first open.
      console.warn("[deck-file] skipping unreadable nugget", path);
      continue;
    }
    if (!record?.nuggetId || !record.slide) continue;
    const prefix = `nuggets/${rest}/`;
    const bodies = (kind: string): Record<string, unknown> => {
      const out: Record<string, unknown> = {};
      for (const [p2, raw2] of Object.entries(entries)) {
        if (!p2.startsWith(`${prefix}${kind}/`) || !p2.endsWith(".json"))
          continue;
        const docId = p2.slice(`${prefix}${kind}/`.length, -".json".length);
        try {
          out[docId] = JSON.parse(strFromU8(raw2));
        } catch {
          console.warn("[deck-file] skipping unreadable nugget body", p2);
        }
      }
      return out;
    };
    nuggets.push({
      record,
      grids: bodies("grids") as Record<string, GriddDocument>,
      diagrams: bodies("diagrams") as Record<string, DiagramDocument>,
      gantts: bodies("gantts") as Record<string, GanttDocument>,
      charts: bodies("charts") as Record<string, ChartDocument>,
      assets: nuggetAssetsById.get(record.nuggetId) ?? [],
    });
  }

  return { deck, grids, diagrams, gantts, charts, assets, nuggets };
}

// §12.6.2 Import a `.deckai` file as a NEW deck.
//
// §17.5.3 `adoptedNuggets` is how many of the archive's nuggets this database already had and
// therefore KEPT, whatever their rev — the import links to the local copy rather than
// overwriting it, because a file is a snapshot someone mailed you and not a sync channel. It
// is returned rather than logged because the user needs to be told: their deck now shares
// slides with their own existing shelf, which is usually what they wanted and is never
// something they should have to discover.
export async function importDeckFile(
  file: File,
): Promise<{ deckId: string; adoptedNuggets: number }> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const bundle = zipToBundle(bytes);
  const deckId = await writeDeckBundle(remapBundle(bundle));
  return { deckId, adoptedNuggets: takeAdoptedNuggets().length };
}
